import React, { HTMLAttributes, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animated, useInView, useTrail } from "react-spring";
import { twMerge } from "tailwind-merge";
import { ROUTE_PATHS } from "../../../routes/paths/route-paths";
import BaseButton from "../../atoms/baseButton";
import SectionTitle from "../../atoms/sectionTitle";

interface IJoinAsgardSection {}

type TJoinAsgardSectionProps = IJoinAsgardSection &
  HTMLAttributes<HTMLDivElement>;

const JoinAsgardSection: React.FC<TJoinAsgardSectionProps> = (props) => {
  const { ...otherProps } = props;
  const [ref, inView] = useInView({
    amount: 0.4,
    once: false,
  });

  // lazy load video src and allow switching via env
  const [srcUrl, setSrcUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (inView && !srcUrl) {
      // set the src only when in view to avoid initial full download
      setSrcUrl(`https://dqyerp2t68wdu.cloudfront.net/join_asgard.mp4`);
      // try autoplay (muted allowed)
      setTimeout(() => videoRef.current?.play().catch(() => {}), 100);
    }
    if (!inView) {
      videoRef.current?.pause();
    }
  }, [inView, srcUrl]);

  const [textRef, textSprings] = useInView(() => ({
    from: {
      opacity: 0,
      x: 400,
    },
    to: {
      opacity: 1,
      x: 0,
    },
    rootMargin: "-80%",
    once: false,
    config: { tension: 200, friction: 25 },
    delay: 1000,
  }));

  const trailSprings = useTrail(5, {
    from: {
      transform: "translateY(0%)",
      opacity: 1,
      backgroundColor: "black",
      height: "100%",
    },
    to: inView
      ? {
          transform: "translateY(-60%)",
          opacity: 0,
          backgroundColor: "transparent",
          height: "10%",
        }
      : {
          transform: "translateY(0%)",
          opacity: 1,
          backgroundColor: "black",
          height: "100%",
        },
    config: { tension: 200, friction: 40 },
  });

  return (
    <div
      className="justify-center items-center flex flex-col p-8 laptop:py-10 laptop:px-[10%] overflow-hidden"
      {...otherProps}
    >
      <SectionTitle
        chineseTitle="加入ASGARD EV 網絡"
        englishTitle="Join ASGARD EV"
      />
      <div className="flex flex-col laptop:flex-row gap-5 laptop:gap-10 items-center justify-center">
        <div className="relative w-full laptop:w-1/2" ref={ref}>
          <video
            ref={videoRef}
            // controls
            preload="metadata"
            className="aspect-video w-full"
            muted // Ensure the video is muted
            loop
            playsInline
            src={srcUrl || undefined}
          ></video>
          {/* <img
            src={require("../../../assets/images/join_cover.png")}
            className="w-[220px] m-auto aspect-square laptop:w-full laptop:aspect-square"
            alt="join_us"
          /> */}
          {trailSprings.map((style, index) => {
            // const isEven = index % 2 === 0;
            const leftValue = `${index * 20}%`;
            return (
              <animated.div
                key={index}
                className={twMerge(
                  `absolute top-0 w-[21%] bg-black border-black`
                )}
                style={{ left: leftValue, ...style }}
              ></animated.div>
            );
          })}
        </div>

        <div
          ref={textRef}
          className="flex w-full laptop:w-1/2 flex-col text-white text-sm laptop:text-base gap-4"
        >
          <animated.span style={textSprings}>
            你可以加入 ASGARD EV 的網絡，在你的屋苑、商場、工商廈的停車場安裝
            ASGARD EV
            充電站。在吸引車流、人流，並賺取充電收入外、提升物業形象及價值。
          </animated.span>
          <animated.span style={textSprings}>
            立即與我們聯絡，由專人為你提供充電站計畫咨詢！
          </animated.span>
          <animated.span
            style={textSprings}
            className="text-sm laptop:text-base"
          >
            Join ASGARD EV network. Install ASGARD EV charging stations in your
            housing estates, shopping malls, and industrial/commercial
            buildings' parking lots. Attract traffic, people, earn income,
            enhance property image and value.
          </animated.span>
          <animated.span
            style={textSprings}
            className="text-sm laptop:text-base"
          >
            Contact us for consultation!
          </animated.span>
          <div className="flex flex-row gap-4">
            <Link to={ROUTE_PATHS.CONTACT_US_PAGE} className="laptop:w-fit">
              <BaseButton className="w-full laptop:w-[10.75rem]">
                <div className="flex flex-col text-white">
                  <span>聯絡我們</span>
                  <span>Contact Us</span>
                </div>
              </BaseButton>
            </Link>

            {false && (
              <Link to={ROUTE_PATHS.CONTACT_US_PAGE} className="laptop:w-fit">
                <BaseButton
                  className="w-full laptop:w-[10.75rem]"
                  colorBase="gray"
                >
                  <div className="flex flex-col text-white">
                    <span>常見問題</span>
                    <span>FAQ</span>
                  </div>
                </BaseButton>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinAsgardSection;
